import { useState, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import { allMangaList, categories, sortOptions } from '@/mocks/manga';
import type { Manga } from '@/mocks/manga';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'popular');
  const itemsPerPage = 8;

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredManga: Manga[] = useMemo(() => {
    let result = [...allMangaList];

    if (slug && slug !== 'all') {
      if (slug === 'completed') {
        result = result.filter((m) => m.status === 'completed');
      } else {
        result = result.filter((m) => m.category === slug);
      }
    }

    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'latest':
        result.sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
        break;
      case 'chapters':
        result.sort((a, b) => b.totalChapters - a.totalChapters);
        break;
    }

    return result;
  }, [slug, sortBy]);

  const totalPages = Math.ceil(filteredManga.length / itemsPerPage);
  const paginatedManga = filteredManga.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleCategoryChange = (newSlug: string) => {
    setCurrentPage(1);
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": `${currentCategory.name} - 咻咻漫画`,
          "description": `咻咻漫画${currentCategory.name}分类，汇集最全${currentCategory.name}资源，全部免费下拉式畅读。`,
          "url": `https://htb88.cn/category/${slug}`,
          "inLanguage": "zh-CN"
        })}
      </script>

      <div className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={[{ label: currentCategory.name }]} />

          <div className="mt-6">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">
              {currentCategory.name}
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              共 {filteredManga.length} 部作品
            </p>
          </div>

          {/* Category Tabs */}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Sort Options */}
          <div className="mt-4 flex items-center gap-3 pb-4 border-b border-background-200/70">
            <span className="text-sm text-foreground-500 whitespace-nowrap">{t('category.filter_by')}:</span>
            <div className="flex items-center gap-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => { setSortBy(opt.value); setCurrentPage(1); }}
                  className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${
                    sortBy === opt.value
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {opt.name}
                </button>
              ))}
            </div>
          </div>

          {/* Manga Grid */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop>
            {paginatedManga.map((manga) => (
              <MangaCard key={manga.id} manga={manga} />
            ))}
          </div>

          {filteredManga.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center text-foreground-300">
                <i className="ri-emotion-sad-line text-4xl"></i>
              </div>
              <p className="mt-4 text-foreground-500">暂无该分类的漫画作品</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all cursor-pointer ${
                    page === currentPage
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}